import React from 'react';

import { retrievePersistentData } from '@smart-tv/utils/persistent-data/persistent-data';
import { getAppTextKMM, appTextKeys } from '@smart-tv/utils/kmm/getAppText';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

export interface GdprText {
  title: string;
  main: string;
  last: string;
  link: string;
}

export interface GDPRContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface GDPRContextValues {
  firstTimeLaunch: boolean;
  setFirstTimeLaunch: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
  gdprText?: GdprText;
  handleOpen: () => void;
  handleClose: () => void;
}

export const GDPRContext = React.createContext<GDPRContextValues>({} as GDPRContextValues);

export const GDPRConsumer = GDPRContext.Consumer;

export const GDPRProvider = (props: GDPRContextProps) => {
  const { children } = props;

  const acceptedGDPR = retrievePersistentData<boolean>('accepted-gdpr');

  const [firstTimeLaunch, setFirstTimeLaunch] = React.useState(!(acceptedGDPR || IS_BROWSER));
  const [open, setOpen] = React.useState(false);
  const [gdprText, setGdprText] = React.useState<GdprText>();

  const handleOpen = React.useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const getGDPRText = React.useCallback(async () => {
    try {
      if (gdprText) {
        return;
      }
      const kmmData = await getAppTextKMM(appTextKeys.gdpr);

      setGdprText(kmmData);
      if (firstTimeLaunch) {
        setOpen(true);
      }
    } catch (error) {
      setGdprText({
        title: 'Kunne ikke hente vores persondatapolitik'
      } as GdprText);
    }
  }, [gdprText, firstTimeLaunch]);

  React.useEffect(() => {
    if (!gdprText) {
      getGDPRText();
    }
  }, [gdprText, getGDPRText]);

  return (
    <GDPRContext.Provider
      value={{ firstTimeLaunch, setFirstTimeLaunch, open, handleOpen, handleClose, gdprText }}
    >
      {children}
    </GDPRContext.Provider>
  );
};

export default GDPRContext;
